/**
 * Persists one element's geometry after a group gesture ends.
 *
 * This deliberately does NOT replace the commit bodies inside use-resizer. Those two
 * sites look alike but are not: stopResize dispatches updateStyle before taking the
 * mirror snapshot, while mouseUp dispatches it inside the snapshot callback, after
 * saveMirrorToCloudinary. Unifying them would have to impose one of those orders on
 * the other site, changing behaviour that must stay exactly as it is. So this is a
 * third caller with its own ordering, used only by the group overlay.
 *
 * Master-slide propagation is not handled here. Groups are disabled on master slides,
 * so nothing needs it.
 */

import { emitStyleUpdate } from '../Socket/Socket';
import { slideAction } from '../store/slideSlice';
import { slideAPI } from '../store/api';
import { isStyleChanged, updateElementStyle } from './helper';
import { resolveElementType, getExistingData } from './elementStyleUtils';

/**
 * Commit one member's style. Returns the updated slides object so a caller committing
 * several members can thread it into the next call, or null when the style was
 * unchanged and nothing was persisted.
 *
 * The caller is responsible for the mirror snapshot: a group gesture moves many
 * members at once and takes a single snapshot afterwards, rather than one per member.
 *
 * `persistDocument` exists for the same reason. updateElementStyleInDatabase always
 * POSTs the element's style, but only runs the debounced whole-document save when it
 * is given a dispatch. A group gesture wants every style posted and exactly one
 * document save — the last one, carrying the accumulated slides — so earlier members
 * pass false and the API call receives null in place of dispatch.
 */
export const commitElementStyle = ({
  eleId,
  updatedStyle,
  currentSlideId,
  existingSlides,
  mirrorSlides,
  presentationId,
  dispatch,
  persistDocument = true,
}) => {
  const type = resolveElementType(eleId);
  const existingData = getExistingData(type, currentSlideId, eleId, existingSlides);
  if (!existingData || !isStyleChanged(existingData.styles, updatedStyle)) return null;

  emitStyleUpdate({
    roomId: presentationId,
    elementData: { slideId: currentSlideId, textBoxId: eleId, updatedStyle },
  });

  dispatch(slideAction.updateStyle({ slideId: currentSlideId, textBoxId: eleId, updatedStyle }));

  const updatedSlides = updateElementStyle(
    existingSlides,
    { textBoxId: eleId, updatedStyle, currentSlideId },
    type,
  );
  slideAPI.updateElementStyleInDatabase(
    presentationId, currentSlideId, eleId, updatedStyle, type,
    persistDocument ? dispatch : null,
    { slides: updatedSlides, mirrorSlides },
  );

  return updatedSlides;
};

export default commitElementStyle;
